import React, { Fragment, useMemo } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

function AppActivity({
  data, hourBlock, additionalProperty, projects = [],
}) {
  const additionalData = useMemo(() => data[additionalProperty], [data, additionalProperty]);

  const getProjectColor = (name) => {
    if (!name || name === 'No Project') return '#6B7280';
    const colors = [
      '#3225e7ff', '#F97316', '#F59E0B', '#EAB308', '#84CC16',
      '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9',
      '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
      hash = name.charCodeAt(i) + (hash * 33 - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Group additionalData by projectId and aggregate time/session
  const groupedByProject = useMemo(() => {
    if (!additionalData || typeof additionalData !== 'object') return {};

    const groups = {};
    Object.keys(additionalData).forEach((appName) => {
      const appEntries = additionalData[appName];
      const pid = appEntries.projectId || data?.projectId || null;

      if (!groups[pid]) groups[pid] = {};

      if (!groups[pid][appName]) {
        groups[pid][appName] = { timeInSeconds: 0, sessions: 0, projectId: pid };
      }

      // Normalize values
      const time = appEntries.timeInSeconds || appEntries.time || 0;
      const sessions = appEntries.sessions || appEntries.sessionCount || 1;

      groups[pid][appName].timeInSeconds += time;
      groups[pid][appName].sessions += sessions;
    });
    return groups;
  }, [additionalData, data]);

  if (!additionalData) {
    return (
      <div className="rounded-xl border shadow mb-3">
        <div className="p-3 text-danger">
          <strong>No app data available for this hour</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border shadow mb-3">
      <table className="w-100 text-sm text-left" style={{ tableLayout: 'fixed', width: '100%' }}>
        <thead className="border-b">
          <tr>
            <th className="py-2 px-3" colSpan={4}>
              {moment().set({ hours: hourBlock, minutes: 0 })
                .format('ddd, MMM DD, YYYY h:mm A Z')}
            </th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(groupedByProject).map((pid) => {
            const matchingProject = projects.find(
              (p) => p.id === parseInt(pid, 10) || p.id === pid,
            );
            const projectName = matchingProject ? matchingProject.name : 'No Project';

            return (
              <Fragment key={pid}>
                {/* App rows under this project */}
                {Object.keys(groupedByProject[pid]).map((appName) => {
                  const appData = groupedByProject[pid][appName];
                  const timeInSeconds = appData.timeInSeconds || 0;
                  const sessionCount = appData.sessions || 0;

                  return (
                    <tr key={appName} className="cursor-pointer hover:bg-gray-50">
                      {/* Project color bubble */}
                      <td className="py-2 px-3" style={{ width: '25%' }}>
                        <div className="d-flex align-items-center">
                          <div
                          // eslint-disable-next-line
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold me-2"
                            style={{
                              width: '24px',
                              height: '24px',
                              backgroundColor: getProjectColor(projectName),
                              fontSize: '12px',
                              minWidth: '24px',
                              color: 'white',
                            }}
                          >
                            {projectName.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-truncate">{projectName}</span>
                        </div>
                      </td>

                      {/* App name */}
                      <td className="py-2 px-3" style={{ width: '35%' }}>
                        {appName}
                      </td>

                      {/* Time spent */}
                      <td className="py-2 px-3" style={{ width: '25%' }}>
                        {timeInSeconds > 0
                          ? `${Math.floor(timeInSeconds / 3600)
                            .toString()
                            .padStart(2, '0')}:${Math.floor((timeInSeconds % 3600) / 60)
                            .toString()
                            .padStart(2, '0')}:${Math.floor(timeInSeconds % 60)
                            .toString()
                            .padStart(2, '0')}`
                          : '00:00:00'}
                      </td>

                      {/* Session count */}
                      <td className="py-2 px-3" style={{ width: '15%' }}>
                        {sessionCount}
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

AppActivity.propTypes = {
  data: PropTypes.shape({
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  hourBlock: PropTypes.string.isRequired,
  additionalProperty: PropTypes.string.isRequired,
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
};

export default AppActivity;
